/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Follow-up R3 (KDS-QSR-16): productividad de cocina en Informes → Operación.
 * Filtros por estación (hospitality.stations de la sucursal), producto
 * (picker de catálogo, mismo patrón que Ventas) y turno de caja
 * (pos.cash_sessions — el modelo real de "turno"). RPC
 * get_kitchen_productivity_report(branch, from, to, station, product, shift).
 * Se auto-carga con el rango del período (mismo patrón que
 * ReportsModifierConsumptionSection).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { styles } from '../ReportsScreen.styles';
import { spacing } from '../../../shared/theme/spacing';
import { ReportsDenseListCard } from './ReportsDenseListCard';
import { ReportsBlockHeading } from './ReportsBlockHeading';
import { ReportsLoadErrorRow } from './ReportsLoadErrorRow';
import { ReportsVentasProductCatalogPickerModal } from './ReportsVentasProductCatalogPickerModal';
import { OperationalFilterChipScrollRow } from '../../../shared/filters/OperationalFilterPrimitives';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { supabase } from '../../../shared/supabase/supabaseClient';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import {
  getKitchenProductivityReport,
  type KitchenProductivityReport,
} from '../../../hospitality/public-api';
import type { BranchCashSessionReportRow } from '../../domain/reportsTypes';

type StationOption = { id: string; name: string };

const ALL = '__all__';

function formatMinutes(n: number | null | undefined): string {
  return n != null && Number.isFinite(n) ? `${Math.round(n)}m` : '—';
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.reportsDenseListStatItem}>
      <Text style={styles.reportsDenseListStatItemLabel}>{label}</Text>
      <Text style={styles.reportsDenseListStatItemValue}>{value}</Text>
    </View>
  );
}

export function ReportsKitchenProductivitySection({
  from,
  to,
  cashSessions,
}: {
  from: string;
  to: string;
  cashSessions: BranchCashSessionReportRow[];
}) {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const [stations, setStations] = useState<StationOption[]>([]);
  const [stationId, setStationId] = useState<string | null>(null);
  const [product, setProduct] = useState<{ id: string; name: string } | null>(null);
  const [shiftId, setShiftId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [report, setReport] = useState<KitchenProductivityReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Estaciones de la sucursal (RLS org en hospitality.stations).
  useEffect(() => {
    if (!currentBranchId) return;
    let cancelled = false;
    void supabase
      .schema('hospitality')
      .from('stations')
      .select('id, name')
      .eq('branch_id', currentBranchId)
      .is('deleted_at', null)
      .order('sort_order')
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.warn('ReportsKitchenProductivitySection stations', error);
          return;
        }
        setStations(
          (data ?? []).map((row) => ({ id: String(row.id), name: String(row.name ?? 'Estación') })),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [currentBranchId]);

  useEffect(() => {
    if (!currentBranchId) return;
    setLoading(true);
    setLoadError(false);
    void getKitchenProductivityReport(currentBranchId, from, to, {
      stationId,
      productId: product?.id ?? null,
      shift: shiftId,
    })
      .then((data) => setReport(data))
      .catch((e) => {
        console.warn('ReportsKitchenProductivitySection', e);
        setReport(null);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [currentBranchId, from, to, stationId, product?.id, shiftId, reloadTick]);

  const shiftOptions = useMemo(() => {
    const base = [{ value: ALL, label: 'Todos', accessibilityLabel: 'Todos los turnos' }];
    for (const s of cashSessions) {
      const label = `${formatLocalDateTime(s.openedAt)}${s.status === 'open' ? ' · abierto' : ''}`;
      base.push({ value: s.id, label, accessibilityLabel: `Turno ${label}` });
    }
    return base;
  }, [cashSessions]);

  if (!currentBranchId) return null;

  const byStation = report?.by_station ?? [];
  const slaTotal = (report?.sla_met_count ?? 0) + (report?.sla_missed_count ?? 0);

  return (
    <View style={{ marginTop: spacing.md }}>
      <ReportsBlockHeading
        label="Productividad de cocina"
        description="Comandas y tiempos de preparación del período. Podés filtrar por estación, producto y turno."
      />

      <ReportsDenseListCard>
        {stations.length > 0 ? (
          <View style={{ marginBottom: spacing.xs }}>
            <Text style={styles.caption}>Estación</Text>
            <OperationalFilterChipScrollRow
              ticketTone
              options={[
                { value: ALL, label: 'Todas', accessibilityLabel: 'Todas las estaciones' },
                ...stations.map((s) => ({
                  value: s.id,
                  label: s.name,
                  accessibilityLabel: `Estación ${s.name}`,
                })),
              ]}
              value={stationId ?? ALL}
              onChange={(id) => setStationId(id === ALL ? null : id)}
              testIDPrefix="informes.cocina.estacion"
            />
          </View>
        ) : null}

        {cashSessions.length > 0 ? (
          <View style={{ marginBottom: spacing.xs }}>
            <Text style={styles.caption}>Turno de caja</Text>
            <OperationalFilterChipScrollRow
              ticketTone
              options={shiftOptions}
              value={shiftId ?? ALL}
              onChange={(id) => setShiftId(id === ALL ? null : id)}
              testIDPrefix="informes.cocina.turno"
            />
          </View>
        ) : null}

        <View style={[styles.reportsDenseListFieldRow, { marginBottom: spacing.xs }]}>
          <Text style={styles.reportsDenseListFieldLabel}>Producto</Text>
          <TouchableOpacity
            onPress={() => setPickerOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Elegir producto para filtrar el informe"
            testID="informes.cocina.producto.pick"
          >
            <Text style={[styles.secondaryButtonText, { color: ticketColors.ink2 }]}>
              {product ? product.name : 'Elegir…'}
            </Text>
          </TouchableOpacity>
        </View>
        {product ? (
          <View style={[styles.ventasPinnedProductChip, { marginBottom: spacing.xs }]}>
            <View style={styles.ventasPinnedProductChipText}>
              <Text style={[styles.caption, { color: ticketColors.ink3 }]} numberOfLines={1}>
                Filtrando por
              </Text>
              <Text style={[styles.secondaryButtonText, { fontWeight: '600' }]} numberOfLines={2}>
                {product.name}
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => setProduct(null)}
              accessibilityRole="button"
              accessibilityLabel="Quitar filtro de producto"
              hitSlop={10}
              testID="informes.cocina.producto.clear"
            >
              <Ionicons name="close-circle" size={24} color={ticketColors.ink2} />
            </TouchableOpacity>
          </View>
        ) : null}
      </ReportsDenseListCard>

      <ReportsDenseListCard wrapStyle={{ marginTop: spacing.sm }}>
        {loading ? (
          <Text style={styles.caption}>Cargando…</Text>
        ) : loadError ? (
          <ReportsLoadErrorRow
            message="No se pudo cargar la productividad de cocina."
            onRetry={() => setReloadTick((t) => t + 1)}
          />
        ) : report ? (
          <>
            <View style={styles.reportsDenseListStatGrid}>
              <StatCell label="Comandas" value={String(report.ticket_count)} />
              <StatCell label="Ítems" value={String(report.item_count)} />
              <StatCell label="Prep. prom." value={formatMinutes(report.avg_prep_minutes)} />
              <StatCell label="Prep. habitual" value={formatMinutes(report.p50_prep_minutes)} />
              <StatCell label="Prep. alta" value={formatMinutes(report.p90_prep_minutes)} />
              <StatCell label="Entrega prom." value={formatMinutes(report.avg_handoff_minutes)} />
              <StatCell
                label="A tiempo"
                value={
                  slaTotal > 0
                    ? `${Math.round((report.sla_met_count / slaTotal) * 100)}%`
                    : '—'
                }
              />
              <StatCell
                label="A tiempo (ok/total)"
                value={`${report.sla_met_count}/${slaTotal}`}
              />
            </View>
            {byStation.length > 0 ? (
              <View style={[styles.reportsDenseListSection, { marginTop: spacing.sm }]}>
                <Text style={styles.reportsSheetBlockTitle}>Por estación</Text>
                {byStation.map((row, idx) => (
                  <View
                    key={row.station_id || idx}
                    style={[
                      styles.reportsDenseListFieldRow,
                      idx > 0 && {
                        borderTopWidth: StyleSheet.hairlineWidth,
                        borderTopColor: ticketColors.rule,
                        paddingTop: spacing.xs,
                      },
                    ]}
                  >
                    <View style={{ flex: 1 }}>
                      <Text style={styles.reportsDenseListFieldLabel}>{row.station_name}</Text>
                      <Text style={styles.caption} numberOfLines={1}>
                        {row.tickets} comandas · {row.items} ítems
                        {row.avg_prep_minutes != null
                          ? ` · prep ~${Math.round(row.avg_prep_minutes)}m`
                          : ''}
                        {row.sla_met_count + row.sla_missed_count > 0
                          ? ` · ${Math.round(
                              (row.sla_met_count / (row.sla_met_count + row.sla_missed_count)) * 100,
                            )}% a tiempo`
                          : ''}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            ) : null}
          </>
        ) : (
          <Text style={styles.caption}>Sin datos de cocina en el período.</Text>
        )}
      </ReportsDenseListCard>

      <ReportsVentasProductCatalogPickerModal
        visible={pickerOpen}
        branchId={currentBranchId}
        onClose={() => setPickerOpen(false)}
        onSelect={(p) => {
          setProduct({ id: p.id, name: p.name });
          setPickerOpen(false);
        }}
      />
    </View>
  );
}
